import { Box, Button, Chip, TextField, Typography } from '@mui/material';
import { useFormik } from 'formik';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import * as yup from 'yup';
import { WrappedTask } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/Task';
import useAuth from 'src/careflow/ph-core/authentication/hook/useAuth';
import useLocales from 'src/careflow/ph-core/internationalization/hook/useLocales';
import { scrollbarStyle } from 'src/utils/utilities';
import SearchTextField from 'src/components/SearchTextField';
import Iconify from 'src/components/Iconify';
import { TaskAnnotationItem } from './task-annotation-utils';
import { getFormattedDate } from 'src/careflow/common/annotation-utils';
import AnnotationsModalListCard from 'src/careflow/common/AnnotationsModalListCard';

export interface TaskAnnotationsEditorProps {
  annotations: TaskAnnotationItem[];
  onAnnotationsChange: (next: TaskAnnotationItem[]) => void;
  task: WrappedTask | null;
  disabled?: boolean;
  showHeader?: boolean;
  /** When true, enter create mode and then parent should reset this to false. */
  triggerCreateMode?: boolean;
  onCreateModeTriggered?: () => void;
}

interface TaskAnnotationFormValues {
  description: string;
  comment: string;
}

const validationSchema = yup.object({
  description: yup.string().required('Description is required'),
  comment: yup.string(),
});

export default function TaskAnnotationsEditor({
  annotations,
  onAnnotationsChange,
  task,
  disabled = false,
  showHeader = true,
  triggerCreateMode = false,
  onCreateModeTriggered,
}: TaskAnnotationsEditorProps) {
  const { i18n } = useLocales();
  const currentUserName = useAuth()?.getCurrentUser?.()?.name ?? '';

  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const [createMode, setCreateMode] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [searchFilter, setSearchFilter] = useState('');
  const [searchTextField, setSearchTextField] = useState('');

  const filteredWithIndex = useMemo(
    () =>
      annotations
        .map((annotation, index) => ({ annotation, index }))
        .filter(
          ({ annotation: a }) =>
            a?.text?.toLowerCase()?.includes(searchFilter?.toLowerCase()) ||
            a?.authorString?.toLowerCase()?.includes(searchFilter?.toLowerCase())
        ),
    [annotations, searchFilter]
  );

  const selectedAnnotation =
    selectedIndex !== null && selectedIndex >= 0 && annotations[selectedIndex]
      ? annotations[selectedIndex]
      : null;

  const buildFormValuesFromAnnotations = useCallback((ann: TaskAnnotationItem[]): TaskAnnotationFormValues => {
    if (!ann.length) return { description: '', comment: '' };
    const description = ann[0]?.text ?? '';
    const comment = ann
      .slice(1)
      .map((a) => a.text)
      .join('\n');
    return { description, comment };
  }, []);

  const splitAnnotationTextToFormValues = useCallback((text: string): TaskAnnotationFormValues => {
    if (!text) return { description: '', comment: '' };
    const idx = text.indexOf('\n\n');
    if (idx === -1) return { description: text, comment: '' };
    return {
      description: text.slice(0, idx),
      comment: text.slice(idx + 2),
    };
  }, []);

  const formik = useFormik<TaskAnnotationFormValues>({
    initialValues: { description: '', comment: '' },
    validationSchema,
    onSubmit: (values) => {
      const desc = values.description.trim();
      if (!desc) return;
      const taskOwner =
        (task as any)?.getOwnerDisplay?.() ?? (task as any)?.getOwnerId?.() ?? '';
      const authorString = taskOwner.trim() || currentUserName;

      if (createMode) {
        const commentTrimmed = (values.comment ?? '').trim();
        const combinedText = commentTrimmed ? `${desc}\n\n${commentTrimmed}` : desc;
        const newItem: TaskAnnotationItem = {
          text: combinedText,
          authorString,
          time: new Date().toISOString(),
        };
        onAnnotationsChange([...annotations, newItem]);
        setCreateMode(false);
        setEditMode(false);
        setSelectedIndex(null);
        formik.resetForm({ values: { description: '', comment: '' } });
      } else if (selectedIndex !== null && selectedIndex >= 0 && selectedIndex < annotations.length) {
        const commentTrimmed = (values.comment ?? '').trim();
        const combinedText = commentTrimmed ? `${desc}\n\n${commentTrimmed}` : desc;
        const existingTime = annotations[selectedIndex]?.time ?? '';
        const updatedAnnotation: TaskAnnotationItem = {
          text: combinedText,
          authorString,
          time: existingTime,
        };
        const nextAnnotations = [...annotations];
        nextAnnotations[selectedIndex] = updatedAnnotation;
        onAnnotationsChange(nextAnnotations);
        setEditMode(false);
        formik.setValues(splitAnnotationTextToFormValues(updatedAnnotation.text));
        formik.setTouched({});
        formik.setErrors({});
      }
    },
  });

  const handleSelectAnnotation = useCallback((index: number) => {
    setCreateMode(false);
    setEditMode(false);
    setSelectedIndex(index);
  }, []);

  const enterCreateMode = useCallback(() => {
    setSelectedIndex(null);
    setCreateMode(true);
    setEditMode(false);
    formik.setValues({ description: '', comment: '' });
    formik.setTouched({});
    formik.setErrors({});
  }, []);

  useEffect(() => {
    if (triggerCreateMode) {
      enterCreateMode();
      onCreateModeTriggered?.();
    }
  }, [triggerCreateMode, enterCreateMode, onCreateModeTriggered]);

  const enterEditMode = useCallback(() => {
    setEditMode(true);
    setCreateMode(false);
    formik.setValues(splitAnnotationTextToFormValues(selectedAnnotation?.text ?? ''));
    formik.setTouched({});
    formik.setErrors({});
  }, [selectedAnnotation?.text, splitAnnotationTextToFormValues]);

  const showFormForCreate = createMode;
  const showFormForEdit = !createMode && selectedIndex !== null && editMode;
  const showForm = showFormForCreate || showFormForEdit;
  const showReadOnly = !createMode && selectedIndex !== null && !editMode;

  const debouncedSearch = useMemo(
    () =>
      (() => {
        let t: ReturnType<typeof setTimeout>;
        return (v: string) => {
          clearTimeout(t);
          t = setTimeout(() => setSearchFilter(v), 300);
        };
      })(),
    []
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 320 }}>
      {showHeader && (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            {i18n('annotations.details.title', 'crs')}
          </Typography>
          <Button
            variant="contained"
            size="small"
            startIcon={<Iconify icon="eva:plus-fill" />}
            onClick={enterCreateMode}
            disabled={disabled}
          >
            {i18n('annotations.details.newAnnotationButton', 'crs')}
          </Button>
        </Box>
      )}

      <Box sx={{ display: 'flex', flex: 1, minHeight: 320 }}>
        {/* Left: list — 35% */}
        <Box
          sx={{
            width: '35%',
            minWidth: 260,
            borderRight: 1,
            borderColor: 'divider',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            pr: 1,
          }}
        >
          <Box sx={{ py: 1.5, px: 0 }}>
            <SearchTextField
              value={searchTextField}
              onChange={(e) => {
                const v = e.target.value;
                setSearchTextField(v);
                debouncedSearch(v);
              }}
              placeholder={i18n('annotations.details.search', 'crs') || 'Search...'}
              fullWidth
              disabled={disabled}
            />
          </Box>
          <Box sx={{ flex: 1, overflowY: 'auto', ...scrollbarStyle, px: 1.5, pb: 1 }}>
            {filteredWithIndex.map(({ annotation, index }) => (
              <Box key={`${index}-${annotation.authorString}-${annotation.time}`} sx={{ mb: 1.5 }}>
                <AnnotationsModalListCard
                  annotation={annotation}
                  onSelect={() => handleSelectAnnotation(index)}
                  selected={selectedIndex === index}
                  authorFallback={currentUserName}
                />
              </Box>
            ))}
            {filteredWithIndex.length === 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                No annotations
              </Typography>
            )}
          </Box>
          <Box sx={{ borderTop: 1, borderColor: 'divider', py: 1.25, px: 1.5 }}>
            <Typography variant="caption" color="text.secondary">
              {annotations.length} {annotations.length === 1 ? 'Annotation' : 'Annotations'}
            </Typography>
          </Box>
        </Box>

        {/* Right: detail / form — 65% */}
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0, overflow: 'hidden' }}>
          {createMode && (
            <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', flex: 1, overflow: 'auto' }}>
              <form onSubmit={formik.handleSubmit} style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                <TextField
                  fullWidth
                  label={`${i18n('patients.details.tasks.description', 'crs')} *`}
                  name="description"
                  value={formik.values.description}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.description && Boolean(formik.errors.description)}
                  helperText={formik.touched.description && formik.errors.description}
                  multiline
                  rows={3}
                  disabled={disabled}
                  sx={{ mb: 2 }}
                />
                <TextField
                  fullWidth
                  label={i18n('patients.details.tasks.comments', 'crs')}
                  name="comment"
                  value={formik.values.comment}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  multiline
                  rows={6}
                  disabled={disabled}
                  sx={{ flex: 1 }}
                />
                <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
                  <Button
                    type="button"
                    variant="outlined"
                    onClick={() => {
                      setCreateMode(false);
                      setEditMode(false);
                      formik.resetForm();
                    }}
                    disabled={disabled}
                  >
                    {i18n('annotations.details.cancelButton', 'crs')}
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    disabled={disabled}
                    onClick={(e) => {
                      e.preventDefault();
                      formik.handleSubmit();
                    }}
                  >
                    {i18n('annotations.details.saveButton', 'crs')}
                  </Button>
                </Box>
              </form>
            </Box>
          )}

          {!createMode && selectedIndex !== null && showReadOnly && (
            <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', flex: 1, overflow: 'auto' }}>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 0.25 }}>
                {i18n('annotations.details.author', 'crs')}
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 600, mb: 1.5 }}>
                {selectedAnnotation?.authorString || currentUserName || '—'}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 0.25 }}>
                {i18n('annotations.details.date', 'crs')}
              </Typography>
              <Typography variant="body2" sx={{ mb: 1.5 }}>
                {getFormattedDate(selectedAnnotation?.time ?? '')}
              </Typography>
              <Chip
                label={i18n('annotations.details.title', 'crs')}
                size="small"
                sx={{ alignSelf: 'flex-start', mb: 2 }}
              />
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', flex: 1 }}>
                {selectedAnnotation?.text ?? ''}
              </Typography>
              <Button
                variant="outlined"
                size="small"
                onClick={enterEditMode}
                disabled={disabled}
                sx={{ mt: 2, alignSelf: 'flex-start' }}
              >
                Edit
              </Button>
            </Box>
          )}

          {!createMode && selectedIndex !== null && showFormForEdit && (
            <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', flex: 1, overflow: 'auto' }}>
              <form onSubmit={formik.handleSubmit} style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                <TextField
                  fullWidth
                  label={`${i18n('patients.details.tasks.description', 'crs')} *`}
                  name="description"
                  value={formik.values.description}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.description && Boolean(formik.errors.description)}
                  helperText={formik.touched.description && formik.errors.description}
                  multiline
                  rows={3}
                  disabled={disabled}
                  sx={{ mb: 2 }}
                />
                <TextField
                  fullWidth
                  label={i18n('patients.details.tasks.comments', 'crs')}
                  name="comment"
                  value={formik.values.comment}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  multiline
                  rows={6}
                  disabled={disabled}
                  sx={{ flex: 1 }}
                />
                <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
                  <Button
                    type="button"
                    variant="outlined"
                    onClick={() => {
                      setEditMode(false);
                      formik.setValues(splitAnnotationTextToFormValues(selectedAnnotation?.text ?? ''));
                      formik.setTouched({});
                      formik.setErrors({});
                    }}
                    disabled={disabled}
                  >
                    {i18n('annotations.details.cancelButton', 'crs')}
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    disabled={disabled}
                    onClick={(e) => {
                      e.preventDefault();
                      formik.handleSubmit();
                    }}
                  >
                    {i18n('annotations.details.saveButton', 'crs')}
                  </Button>
                </Box>
              </form>
            </Box>
          )}

          {!createMode && selectedIndex === null && (
            <Box sx={{ py: 3, display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Select an annotation
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
}
